import {useCallback, useEffect, useState} from 'react'

/**
 * The chat's text size, chosen in the chat header and remembered per browser.
 *
 * Applied as `--ob-chat-font-scale` on `.ai-chat-v2-sidebar`, which rescales the chat's copy of the
 * `--ob-font-size-*` tokens (see ai-chat-v2.scss) — the rest of the app keeps its size. A display
 * preference like the theme, so it is not part of the per-user keys cleared on logout.
 */
export interface ChatFontScaleOption {
  scale: number
  label: string
}

export const CHAT_FONT_SCALE_OPTIONS: readonly ChatFontScaleOption[] = [
  {scale: 0.9, label: 'Small'},
  {scale: 1, label: 'Default'},
  {scale: 1.1, label: 'Large'},
  {scale: 1.2, label: 'Extra large'},
]

export const DEFAULT_CHAT_FONT_SCALE = 1
export const AI_CHAT_FONT_SCALE_KEY = 'ai-chat-font-scale'

/** Validate an untrusted value: only the offered steps are accepted, anything else is the default. */
export function normalizeChatFontScale(value: unknown): number {
  const num = typeof value === 'string' && value.trim() !== '' ? Number(value) : value
  const match = CHAT_FONT_SCALE_OPTIONS.find((o) => o.scale === num)
  return match ? match.scale : DEFAULT_CHAT_FONT_SCALE
}

export function chatFontScaleLabel(scale: number): string {
  return CHAT_FONT_SCALE_OPTIONS.find((o) => o.scale === scale)?.label ?? 'Default'
}

export function readChatFontScale(): number {
  try {
    return normalizeChatFontScale(localStorage.getItem(AI_CHAT_FONT_SCALE_KEY))
  } catch {
    return DEFAULT_CHAT_FONT_SCALE /* storage blocked */
  }
}

export function writeChatFontScale(scale: number): void {
  try {
    localStorage.setItem(AI_CHAT_FONT_SCALE_KEY, String(normalizeChatFontScale(scale)))
  } catch {
    /* ignore quota/private-mode */
  }
}

export interface ChatFontScaleState {
  scale: number
  setScale: (scale: number) => void
}

/** The stored chat text size, kept in sync with other open tabs of the app. */
export function useChatFontScale(): ChatFontScaleState {
  const [scale, setScaleState] = useState(readChatFontScale)

  const setScale = useCallback((next: number) => {
    const normalized = normalizeChatFontScale(next)
    setScaleState(normalized)
    writeChatFontScale(normalized)
  }, [])

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === AI_CHAT_FONT_SCALE_KEY) setScaleState(normalizeChatFontScale(e.newValue))
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  return {scale, setScale}
}
